/**
 * Steam 原始码值详情 —— 展示端消费后端「结果详情字段」的地方。
 *
 * **契约（字段名照抄后端，勿改）**：`steam_eresult`(int) /
 * `steam_eresult_name`(str) / `guard_type`(int) / `step`(str)。
 * 后端缺失即**省略键**（不塞 null 占位），故这里按「键在不在」判定。
 *
 * 三个载体形状不同，一个 helper 统一读法：
 *
 * | 载体 | 详情在哪 |
 * |---|---|
 * | 通知行（API `out.payload`） | `payload.payload`（行载荷里嵌一层 payload） |
 * | 铃铛帧 `notification.created` | 顶层 `data` 键携带同一 payload |
 * | 业务帧 `account.need_code` / `account.login_error` | 顶层 `data` 键携带同一 payload |
 *
 * ⚠️ 业务帧的载荷**在顶层 `data` 键下**，不在帧顶层平铺（后端
 * `notifications/service.py::record_state_change` 的 `pending_sse` 业务帧
 * 形状是 `{"type":…, "op_id":…, "user_id":…, "data": {…}}`）。读错形状
 * 是静默的：详情读不到 → 界面少了「可定位」的后缀，没有红灯。
 *
 * **原始码是协议事实**：不翻译、不吞掉。业务文案负责「人话」，本模块
 * 产出的后缀负责「可定位」——两者一起显示。
 */

/** 后端结果详情字段（缺失即省略键）。 */
export interface SteamCodeDetail {
  steam_eresult?: number;
  steam_eresult_name?: string;
  guard_type?: number;
  step?: string;
}

const DETAIL_KEYS = ['steam_eresult', 'steam_eresult_name', 'guard_type', 'step'] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function extract(rec: Record<string, unknown>): SteamCodeDetail | null {
  if (!DETAIL_KEYS.some((key) => key in rec)) return null;
  const out: SteamCodeDetail = {};
  const eresult = rec['steam_eresult'];
  if (typeof eresult === 'number') out.steam_eresult = eresult;
  const name = rec['steam_eresult_name'];
  if (typeof name === 'string' && name !== '') out.steam_eresult_name = name;
  const guard = rec['guard_type'];
  if (typeof guard === 'number') out.guard_type = guard;
  const step = rec['step'];
  if (typeof step === 'string' && step !== '') out.step = step;
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * 从任意一个载体里读出 Steam 原始码值详情。
 *
 * 探查顺序 = 载体的嵌套深度：铃铛帧的 `data` → 通知行的 `payload`（及其
 * 内层 `payload`）→ 载荷自身的平铺字段。任一层命中即返回。
 */
export function steamCodeDetail(source: unknown): SteamCodeDetail | null {
  const rec = asRecord(source);
  if (rec === null) return null;
  for (const key of ['data', 'payload'] as const) {
    const child = asRecord(rec[key]);
    if (child === null) continue;
    const direct = extract(child);
    if (direct !== null) return direct;
    const grand = asRecord(child['payload']);
    if (grand !== null) {
      const nested = extract(grand);
      if (nested !== null) return nested;
    }
  }
  return extract(rec);
}

/**
 * 详情 → 展示用的后缀文本。
 *
 * - 有 `steam_eresult`：`Steam#88 TWO_FACTOR_CODE_MISMATCH`（缺名则只 `Steam#88`）；
 * - 无 EResult 但有 guard 挑战：`guard_type=4`；
 * - 都没有：null（调用方不显示后缀）。
 */
export function formatSteamCode(detail: SteamCodeDetail | null): string | null {
  if (detail === null) return null;
  if (detail.steam_eresult !== undefined) {
    const name = detail.steam_eresult_name;
    return name === undefined
      ? `Steam#${detail.steam_eresult}`
      : `Steam#${detail.steam_eresult} ${name}`;
  }
  if (detail.guard_type !== undefined) return `guard_type=${detail.guard_type}`;
  return null;
}

/** 一步到位：从载体读详情并格式化（读不到返回 null）。 */
export function steamCodeSuffix(source: unknown): string | null {
  return formatSteamCode(steamCodeDetail(source));
}

/**
 * 这个失败码**是否已经被 EResult 后缀印过**——印过就别再印一遍（ADR 0011 §4）。
 *
 * ⚠️ 判据是「**同一个官方名**」而不是「有没有 EResult」：EResult 后缀印的是
 * `Steam#66 ACCOUNT_LOGON_DENIED_NO_MAIL`——只有**名字**那一段与「代码：…」那行
 * 重复；只有编号（`Steam#66`）时码行仍带着新信息 ⇒ 照印。
 *
 * ⚠️ 这条只对**没有平台词条**的码有意义（有词条 ⇒ 印人话，那与官方名不重复）。
 */
export function steamCodeAlreadyShown(detail: SteamCodeDetail | null, code: string): boolean {
  const name = detail?.steam_eresult_name;
  return name !== undefined && name.toLowerCase() === code.toLowerCase();
}
